import { useState } from 'react';
import { ChevronDown, Check, Plus, Trash2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import type { SessionStatus } from '../types';

interface AccountSwitcherProps {
  currentSession: SessionStatus | null;
  accounts: SessionStatus[];
  isLoading?: boolean;
  onSwitchAccount: (sessionId: number) => Promise<boolean>;
  onRemoveAccount: (sessionId: number) => Promise<boolean>;
  onAddAccount: () => void;
}

export function AccountSwitcher({
  currentSession,
  accounts,
  isLoading,
  onSwitchAccount,
  onRemoveAccount,
  onAddAccount,
}: AccountSwitcherProps) {
  const [accountToDelete, setAccountToDelete] = useState<SessionStatus | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const formatAccountName = (session: SessionStatus) => {
    if (session.telegramFirstName || session.telegramLastName) {
      return [session.telegramFirstName, session.telegramLastName].filter(Boolean).join(' ');
    }
    if (session.telegramUsername) {return `@${session.telegramUsername}`;}
    if (session.telegramPhone) {return session.telegramPhone;}
    return 'Аккаунт';
  };

  const handleDelete = async () => {
    if (!accountToDelete?.id) {return;}
    setIsDeleting(true);
    await onRemoveAccount(accountToDelete.id);
    setIsDeleting(false);
    setAccountToDelete(null);
  };

  if (!currentSession) {return null;}

  const currentName = formatAccountName(currentSession);
  const hasMultipleAccounts = accounts.length > 1;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="w-full justify-between px-3 py-2 h-auto min-w-0"
            disabled={isLoading}
          >
            <div className="flex flex-col items-start min-w-0 overflow-hidden">
              <span className="text-xs text-muted-foreground">Вход как</span>
              <span className="font-medium truncate w-full">{currentName}</span>
            </div>
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ChevronDown className="h-4 w-4 opacity-50" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[240px]">
          {accounts.map((account) => (
            <DropdownMenuItem
              key={account.id}
              className="flex items-center justify-between gap-2"
              onClick={() => {
                if (account.id && !account.isActive) {
                  onSwitchAccount(account.id);
                }
              }}
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {account.isActive ? (
                  <Check className="h-4 w-4 text-primary flex-shrink-0" />
                ) : (
                  <div className="w-4 flex-shrink-0" />
                )}
                <span className="truncate">{formatAccountName(account)}</span>
              </div>
              {hasMultipleAccounts && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 flex-shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAccountToDelete(account);
                  }}
                >
                  <Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                </Button>
              )}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onAddAccount}>
            <Plus className="h-4 w-4 mr-2" />
            Добавить аккаунт
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={!!accountToDelete} onOpenChange={(open) => !open && setAccountToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить аккаунт?</AlertDialogTitle>
            <AlertDialogDescription>
              Вы действительно хотите удалить аккаунт{' '}
              <span className="font-medium">{accountToDelete && formatAccountName(accountToDelete)}</span>?
              Вам потребуется заново пройти авторизацию для этого аккаунта.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className={cn(isDeleting && 'opacity-50')}
            >
              {isDeleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
